/**
 * Logto CIAM Service for LEXE Webchat
 *
 * Uses the official @logto/react SDK for authentication.
 * Handles Contact (customer) authentication via Logto CIAM.
 *
 * Logto = Customer Identity (Contacts)
 * Authentik = Internal Identity (Operators) - NOT used in webchat
 */

import { LogtoConfig, PersistKey } from "@logto/react";
import LogtoClient from "@logto/browser";
import type { Nullable } from "@silverhand/essentials";

// ============================================================================
// Session Storage Implementation
// ============================================================================

/**
 * Custom storage class that uses sessionStorage instead of localStorage.
 * This ensures the session is cleared when the browser is closed.
 */
class SessionBrowserStorage {
  readonly appId: string;

  constructor(appId: string) {
    this.appId = appId;
  }

  getKey(item?: string): string {
    return item ? `logto:${this.appId}:${item}` : `logto:${this.appId}`;
  }

  async getItem(key: PersistKey): Promise<Nullable<string>> {
    return sessionStorage.getItem(this.getKey(key));
  }

  async setItem(key: PersistKey, value: string): Promise<void> {
    sessionStorage.setItem(this.getKey(key), value);
  }

  async removeItem(key: PersistKey): Promise<void> {
    sessionStorage.removeItem(this.getKey(key));
  }
}

/**
 * Custom LogtoClient that uses sessionStorage for session-based auth.
 * Session expires when browser/tab is closed.
 */
export class SessionLogtoClient extends LogtoClient {
  constructor(config: LogtoConfig, unstable_enableCache?: boolean) {
    // Create custom adapter with sessionStorage
    const storage = new SessionBrowserStorage(config.appId);

    // Call parent with custom storage via adapter
    // Note: We're using a workaround since the browser SDK doesn't expose storage config directly
    super(config, unstable_enableCache);

    // Override the internal storage after construction
    // This is a bit hacky but necessary since LogtoClient doesn't expose storage configuration
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).adapter.storage = storage;
  }
}

// ============================================================================
// Configuration
// ============================================================================

/**
 * Check if Logto authentication is enabled
 */
export function isLogtoEnabled(): boolean {
  const endpoint = import.meta.env.VITE_LOGTO_ENDPOINT;
  const appId = import.meta.env.VITE_LOGTO_APP_ID;
  return Boolean(endpoint && appId);
}

/**
 * Get Logto configuration from environment variables
 */
export function getLogtoConfig(): LogtoConfig {
  const endpoint = import.meta.env.VITE_LOGTO_ENDPOINT;
  const appId = import.meta.env.VITE_LOGTO_APP_ID;
  const resource = import.meta.env.VITE_LOGTO_RESOURCE;

  if (!endpoint || !appId) {
    throw new Error(
      "Logto configuration missing. Set VITE_LOGTO_ENDPOINT and VITE_LOGTO_APP_ID",
    );
  }

  return {
    endpoint,
    appId,
    // API Resource for JWT with proper scopes
    resources: resource ? [resource] : undefined,
    // Standard OIDC scopes + organization scope for multi-tenancy
    // Note: API resource scopes (chat:read, chat:write) are granted based on
    // organization roles, not requested directly in OIDC scopes
    scopes: [
      "openid",
      "profile",
      "email",
      "urn:logto:scope:organizations", // Required for organization context in token
      "urn:logto:scope:organization_roles", // Include organization roles in token
    ],
  };
}

// ============================================================================
// Storage Keys (for compatibility with existing code)
// ============================================================================

export const LOGTO_STORAGE_KEYS = {
  // These are managed by Logto SDK, but we might need them for integration
  ACCESS_TOKEN: "logto:accessToken",
  ID_TOKEN: "logto:idToken",
  REFRESH_TOKEN: "logto:refreshToken",
} as const;

// ============================================================================
// Types
// ============================================================================

export interface LogtoUserInfo {
  sub: string;
  email?: string;
  emailVerified?: boolean;
  name?: string;
  username?: string;
  picture?: string;
  // Logto organization (maps to tenant)
  organizationId?: string;
  organizations?: string[];
}
